import React, { useState } from 'react';
import { FaCar, FaRupeeSign, FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CarLoanEMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTenure, setLoanTenure] = useState('');
  const [emi, setEMI] = useState('');
  const [totalInterest, setTotalInterest] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);

  const calculateEMI = () => {
    if (!loanAmount || !interestRate || !loanTenure) return;

    const P = parseFloat(loanAmount);
    const R = parseFloat(interestRate) / 12 / 100;
    const N = parseFloat(loanTenure) * 12;

    const numerator = P * R * Math.pow(1 + R, N);
    const denominator = Math.pow(1 + R, N) - 1;
    const calculatedEMI = numerator / denominator;

    // Calculate monthly breakdown
    const breakdown = [];
    let remainingPrincipal = P;
    for (let i = 0; i < N; i++) {
      const interest = remainingPrincipal * R;
      const principal = calculatedEMI - interest;
      remainingPrincipal -= principal;
      breakdown.push({
        month: i + 1,
        interest,
        principal,
        remainingPrincipal
      });
    }
    setMonthlyBreakdown(breakdown);

    setEMI(calculatedEMI.toFixed(2));
    setTotalInterest(((calculatedEMI * N) - P).toFixed(2));
    setTotalAmount((calculatedEMI * N).toFixed(2));
  };

  const chartData = {
    labels: monthlyBreakdown.map(item => item.month),
    datasets: [
      {
        label: 'Interest Payment',
        data: monthlyBreakdown.map(item => item.interest),
        borderColor: '#ff6b6b',
        tension: 0.1,
      },
      {
        label: 'Principal Payment',
        data: monthlyBreakdown.map(item => item.principal),
        borderColor: '#4ecdc4',
        tension: 0.1,
      },
    ],
  };

  return (
    <div className="bg-gradient-to-br from-blue-100 to-purple-100 min-h-screen p-6">
      <Helmet>
        <title>Car Loan EMI Calculator</title>
        <meta name="description" content="Calculate your car loan EMI, total interest, and payment breakdown with our easy-to-use calculator." />
        <meta property="og:title" content="Car Loan EMI Calculator" />
        <meta property="og:description" content="Calculate your car loan EMI, total interest, and payment breakdown with our easy-to-use calculator." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-800">
            <FaCar className="inline-block mr-2 text-blue-500" />
            Car Loan EMI Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <button className="text-blue-500 hover:text-blue-600">
              <FaInfoCircle className="w-6 h-6" />
            </button>
            <button className="text-blue-500 hover:text-blue-600">
              <FaQuestionCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Loan Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter loan amount"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Interest Rate (% p.a.)
            </label>
            <input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="Enter interest rate"
            />
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Loan Tenure (Years)
            </label>
            <input
              type="number"
              value={loanTenure}
              onChange={(e) => setLoanTenure(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="Enter loan tenure"
            />
          </div>
        </div>

        <button
          onClick={calculateEMI}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-green-500 font-bold py-3 px-6 rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:ring-offset-2 transition-all duration-200"
        >
          Calculate EMI
        </button>

        {emi && (
          <div className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-blue-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-blue-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {emi}
                </h3>
                <p className="text-gray-600">Monthly EMI</p>
              </div>

              <div className="bg-blue-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-blue-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {totalInterest}
                </h3>
                <p className="text-gray-600">Total Interest</p>
              </div>

              <div className="bg-blue-100 p-6 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-blue-600">
                  <FaRupeeSign className="inline-block mr-1" />
                  {totalAmount}
                </h3>
                <p className="text-gray-600">Total Amount</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold mb-4">Payment Breakdown</h3>
              <div className="w-full h-[400px]">
                <Line data={chartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: {
                          size: 14
                        }
                      }
                    },
                    title: {
                      display: true,
                      text: 'Monthly Payment Breakdown',
                      font: {
                        size: 16
                      }
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.dataset.label;
                          const value = context.parsed.y;
                          return `${label}: ₹${value.toLocaleString()}`;
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      title: {
                        display: true,
                        text: 'Month',
                        font: {
                          size: 14
                        }
                      },
                      ticks: {
                        font: {
                          size: 12
                        },
                        autoSkip: true,
                        maxTicksLimit: 20
                      }
                    },
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Amount (₹)',
                        font: {
                          size: 14
                        }
                      },
                      ticks: {
                        callback: function(value) {
                          return '₹' + value.toLocaleString();
                        },
                        font: {
                          size: 12
                        }
                      },
                      grid: {
                        drawBorder: false
                      }
                    }
                  },
                  animation: {
                    duration: 1000,
                    easing: 'easeInOutQuad'
                  }
                }} />
              </div>
            </div>
          </div>
        )}

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">Features & Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Accurate Calculations</h4>
              <p className="text-gray-600">Get precise EMI calculations based on your loan amount, interest rate, and tenure.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Visual Insights</h4>
              <p className="text-gray-600">Understand your payment breakdown with our interactive graph showing interest vs principal.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">Real-time Updates</h4>
              <p className="text-gray-600">Instantly see how changes in loan parameters affect your EMI and total payments.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">User-friendly Interface</h4>
              <p className="text-gray-600">Clean and intuitive design that makes it easy to calculate and understand your car loan EMI.</p>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">How to Use</h3>
          <ol className="list-decimal list-inside space-y-4">
            <li>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">Enter Loan Details</h4>
                <p className="text-gray-600">Input your loan amount, interest rate, and tenure in years.</p>
              </div>
            </li>
            <li>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">Calculate EMI</h4>
                <p className="text-gray-600">Click the "Calculate EMI" button to get your monthly EMI amount.</p>
              </div>
            </li>
            <li>
              <div className="bg-blue-50 p-4 rounded-xl">
                <h4 className="text-xl font-semibold mb-2">View Breakdown</h4>
                <p className="text-gray-600">Check your total interest and total amount payable, and view the payment breakdown graph.</p>
              </div>
            </li>
          </ol>
        </div>

        <div className="mt-12">
          <h3 className="text-2xl font-bold mb-6">FAQ</h3>
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">What is EMI?</h4>
              <p className="text-gray-600">EMI stands for Equated Monthly Installment. It's the fixed amount you pay every month to repay your car loan.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">How is EMI calculated?</h4>
              <p className="text-gray-600">EMI is calculated using the formula: EMI = [P x R x (1+R)^N]/[(1+R)^N-1], where P is the loan amount, R is the monthly interest rate, and N is the number of months.</p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-2">What does the graph show?</h4>
              <p className="text-gray-600">The graph shows how your monthly payment is split between interest and principal over the loan tenure. Initially, more of your payment goes towards interest, but this gradually shifts towards principal as the loan matures.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarLoanEMICalculator;
